// src/Components/moderation/ContentManagementPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Search, Trash2, Flag, RefreshCw, AlertTriangle, CheckSquare, Square, EyeOff, Eye } from 'lucide-react';
import { dashboardAPI, moderationAPI, apiFetch } from '../../services/api';

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

const TYPE_FILTERS = [
  { value: '', label: 'All Types' },
  { value: 'journal', label: 'Journal' },
  { value: 'blog', label: 'Blog' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'qna', label: 'Q&A' },
];

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'removed', label: 'Removed' },
];

const PAGE_SIZE = 15;

export default function ContentManagementPage() {
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reportedOnly, setReportedOnly] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [page, setPage]           = useState(1);
  const [selected, setSelected]   = useState(new Set());
  const [bulkActioning, setBulkActioning] = useState(false);
  const [reportedIds, setReportedIds]     = useState(new Set());

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getContentAnalytics('all');
      if (res.success) {
        setPosts(res.data?.topPosts || res.data?.posts || []);
      }

      // Cross-reference with reports to flag reported posts
      try {
        const reportsRes = await moderationAPI.listReports({ content_type: 'post', limit: 100 });
        if (reportsRes.success) {
          const ids = new Set((reportsRes.data.reports || []).map(r => r.content_id));
          setReportedIds(ids);
        }
      } catch { /* reports endpoint may be empty, non-fatal */ }
    } catch (err) {
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const filtered = useMemo(() => {
    return posts.filter(p => {
      if (typeFilter && p.type !== typeFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (reportedOnly && !reportedIds.has(p.id)) return false;
      if (search && !p.title?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [posts, typeFilter, statusFilter, reportedOnly, reportedIds, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [typeFilter, statusFilter, reportedOnly, search]);

  const updateLocalPostStatus = (postId, status) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, status } : p));
  };

  const handleHidePost = async (postId) => {
    setActioningId(postId);
    try {
      await moderationAPI.hidePost(postId);
      updateLocalPostStatus(postId, 'hidden');
    } catch (err) {
      alert(`Hide failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const handleRestorePost = async (postId) => {
    setActioningId(postId);
    try {
      await moderationAPI.restorePost(postId);
      updateLocalPostStatus(postId, 'published');
    } catch (err) {
      alert(`Restore failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const handleBulkHide = async () => {
    if (!window.confirm(`Hide ${selected.size} post(s)? They'll be removed from public view but not deleted.`)) return;
    setBulkActioning(true);
    try {
      await Promise.all(Array.from(selected).map(id => moderationAPI.hidePost(id)));
      selected.forEach(id => updateLocalPostStatus(id, 'hidden'));
      setSelected(new Set());
    } catch (err) {
      alert(`Bulk hide failed: ${err}`);
    } finally {
      setBulkActioning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = paged.map(p => p.id);
    const allSelected = pageIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} post(s)? This cannot be undone.`)) return;
    setBulkActioning(true);
    try {
      await Promise.all(
        Array.from(selected).map(id => apiFetch(`/posts/${id}`, { method: 'DELETE' }))
      );
      setSelected(new Set());
      await fetchPosts();
    } catch (err) {
      alert(`Bulk delete failed: ${err}`);
    } finally {
      setBulkActioning(false);
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return 'Unknown';
    const days = Math.floor((Date.now() - new Date(ts)) / (1000 * 60 * 60 * 24));
    if (days < 1) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <FileText className="text-red-600" size={24} />
            Content Management
          </h1>
          <p className="text-sm text-gray-500">Browse, filter, and bulk-manage all posts</p>
        </div>
        <button onClick={fetchPosts}
          className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-red-400 transition">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 space-y-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search post titles..."
            className="pl-9 pr-3 py-2 w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_TABS.map(f => {
            const count = f.value === '' ? posts.length : posts.filter(p => p.status === f.value).length;
            return (
              <button key={f.value} onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  statusFilter === f.value ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {f.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {TYPE_FILTERS.map(f => (
            <button key={f.value} onClick={() => setTypeFilter(f.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                typeFilter === f.value ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
          <button onClick={() => setReportedOnly(r => !r)}
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition ${
              reportedOnly ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            <Flag size={11} /> Reported Only
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bg-gray-800 text-white rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
          <span className="text-sm">{selected.size} post(s) selected</span>
          <div className="flex gap-2">
            <button onClick={handleBulkHide} disabled={bulkActioning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg text-xs font-medium transition disabled:opacity-50">
              <EyeOff size={13} /> Hide Selected
            </button>
            <button onClick={handleBulkDelete} disabled={bulkActioning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs font-medium transition disabled:opacity-50">
              <Trash2 size={13} /> Delete Selected
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition">
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        {loading ? (
          <div className="space-y-3">{Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No posts match these filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b font-semibold">
                    <th className="pb-3 w-8">
                      <button onClick={toggleSelectAllOnPage}>
                        {paged.every(p => selected.has(p.id)) && paged.length > 0
                          ? <CheckSquare size={16} className="text-red-500" />
                          : <Square size={16} className="text-gray-300" />}
                      </button>
                    </th>
                    <th className="pb-3">Title</th>
                    <th className="pb-3 text-center">Type</th>
                    <th className="pb-3 text-center">Status</th>
                    <th className="pb-3 text-center">Reports</th>
                    <th className="pb-3 text-center">Engagement</th>
                    <th className="pb-3 text-center">Created</th>
                    <th className="pb-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paged.map(p => (
                    <tr key={p.id} className={reportedIds.has(p.id) ? 'bg-orange-50/50' : ''}>
                      <td className="py-3">
                        <button onClick={() => toggleSelect(p.id)}>
                          {selected.has(p.id)
                            ? <CheckSquare size={16} className="text-red-500" />
                            : <Square size={16} className="text-gray-300" />}
                        </button>
                      </td>
                      <td className="py-3 font-medium max-w-xs truncate">{p.title || 'Untitled'}</td>
                      <td className="py-3 text-center">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full capitalize">{p.type || 'N/A'}</span>
                      </td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          p.status === 'published' ? 'bg-green-50 text-green-600' :
                          p.status === 'hidden' ? 'bg-amber-50 text-amber-600' :
                          p.status === 'removed' ? 'bg-red-50 text-red-600' :
                          'bg-gray-100 text-gray-500'
                        }`}>{p.status || 'N/A'}</span>
                      </td>
                      <td className="py-3 text-center">
                        {reportedIds.has(p.id) ? (
                          <span className="flex items-center justify-center gap-1 text-orange-600 text-xs font-medium">
                            <Flag size={11} /> Reported
                          </span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="py-3 text-center">{p.totalEngagement ?? ((p.likes || 0) + (p.dislikes || 0))}</td>
                      <td className="py-3 text-center text-xs text-gray-400">{timeAgo(p.date_created)}</td>
                      <td className="py-3 text-center">
                        {p.status === 'hidden' ? (
                          <button disabled={actioningId === p.id} onClick={() => handleRestorePost(p.id)}
                            className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 rounded-lg text-xs font-medium hover:bg-green-100 transition disabled:opacity-50 mx-auto">
                            <Eye size={11} /> Restore
                          </button>
                        ) : (
                          <button disabled={actioningId === p.id} onClick={() => handleHidePost(p.id)}
                            className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-lg text-xs font-medium hover:bg-amber-100 transition disabled:opacity-50 mx-auto">
                            <EyeOff size={11} /> Hide
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </p>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button key={n} onClick={() => setPage(n)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition ${
                        page === n ? 'bg-red-600 text-white' : 'border border-gray-200 hover:border-red-400'
                      }`}>{n}</button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          <strong>Note:</strong> Bulk delete currently calls the existing <code>DELETE /api/v1/posts/:id</code>
          endpoint directly. A dedicated admin bulk-delete/flag endpoint (with audit logging) should replace this
          once the CrudService rewrite is done.
        </p>
      </div>
    </div>
  );
}